export interface CompressedImage {
  fullBlob: Blob;
  thumbBlob: Blob;
  width: number;
  height: number;
}

interface WorkerResponse {
  ok: boolean;
  result?: CompressedImage;
  message?: string;
}

const FULL_ATTEMPTS = [
  { maxEdge: 1600, quality: 0.78 },
  { maxEdge: 1600, quality: 0.72 },
  { maxEdge: 1400, quality: 0.72 }
];
const TARGET_FULL_BYTES = 900 * 1024;
const THUMB_MAX_EDGE = 400;
const THUMB_QUALITY = 0.7;

export async function compressImage(file: File | Blob): Promise<CompressedImage> {
  if (typeof Worker !== "undefined") {
    try {
      return await compressImageInWorker(file);
    } catch (error) {
      console.warn("Image worker failed, using main-thread fallback.", error);
    }
  }
  await yieldToPaint();
  return compressImageOnMainThread(file);
}

async function compressImageInWorker(file: File | Blob): Promise<CompressedImage> {
  const worker = new Worker(new URL("./imageCompression.worker.ts", import.meta.url), {
    type: "module"
  });
  try {
    const response = await new Promise<WorkerResponse>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        reject(new Error("Image compression timed out."));
      }, 30_000);
      worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
        window.clearTimeout(timeout);
        resolve(event.data);
      };
      worker.onerror = (event) => {
        window.clearTimeout(timeout);
        reject(new Error(event.message || "Image compression worker failed."));
      };
      worker.postMessage({ file });
    });
    if (!response.ok || !response.result) {
      throw new Error(response.message || "Image compression failed.");
    }
    return response.result;
  } finally {
    worker.terminate();
  }
}

export async function compressImageOnMainThread(file: File | Blob): Promise<CompressedImage> {
  const bitmap = await createBitmap(file);
  try {
    let full = await resizeWithCanvas(bitmap, FULL_ATTEMPTS[0].maxEdge, FULL_ATTEMPTS[0].quality);
    for (let index = 1; index < FULL_ATTEMPTS.length && full.blob.size > TARGET_FULL_BYTES; index += 1) {
      const attempt = FULL_ATTEMPTS[index];
      full = await resizeWithCanvas(bitmap, attempt.maxEdge, attempt.quality);
      await yieldToPaint();
    }
    const thumb = await resizeWithCanvas(bitmap, THUMB_MAX_EDGE, THUMB_QUALITY);
    return {
      fullBlob: full.blob,
      thumbBlob: thumb.blob,
      width: full.width,
      height: full.height
    };
  } finally {
    bitmap.close?.();
  }
}

async function createBitmap(file: File | Blob): Promise<ImageBitmap> {
  return createImageBitmap(file, { imageOrientation: "from-image" } as ImageBitmapOptions);
}

async function resizeWithCanvas(
  bitmap: ImageBitmap,
  maxEdge: number,
  quality: number
): Promise<{ blob: Blob; width: number; height: number }> {
  const ratio = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * ratio));
  const height = Math.max(1, Math.round(bitmap.height * ratio));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", {
    alpha: false,
    desynchronized: true
  });
  if (!context) {
    throw new Error("Canvas is not available on this device.");
  }
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(bitmap, 0, 0, width, height);
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (value) => {
        if (value) {
          resolve(value);
        } else {
          reject(new Error("Could not encode image."));
        }
      },
      "image/jpeg",
      quality
    );
  });
  return { blob, width, height };
}

function yieldToPaint(): Promise<void> {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      window.setTimeout(resolve, 0);
    });
  });
}
